import { callCoinGeckoAPI } from './api';
import { supabase } from '../lib/supabase/client';

const CACHE_KEY = 'btc_price_history_cache';

interface BTCPriceCache {
  [date: string]: number; // Format: "YYYY-MM-DD": price
}

interface CacheData {
  prices: BTCPriceCache;
  lastUpdated: number;
}

/**
 * BTC Price History Service
 *
 * Priority order for fetching prices:
 * 1. Supabase (if authenticated) - shared across devices
 * 2. localStorage cache - local fallback
 * 3. CoinGecko API - external source
 *
 * When a price is fetched from CoinGecko, it's saved to both Supabase and localStorage
 */
export const BTCPriceHistoryService = {
  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      return !!session;
    } catch {
      return false;
    }
  },

  /**
   * Get cached BTC prices from localStorage
   */
  getLocalCache(): BTCPriceCache {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (!cached) return {};

      const data: CacheData = JSON.parse(cached);
      return data.prices || {};
    } catch (error) {
      console.error('Error reading BTC price cache:', error);
      return {};
    }
  },

  /**
   * Save prices to localStorage cache
   */
  saveLocalCache(prices: BTCPriceCache): void {
    try {
      const data: CacheData = {
        prices,
        lastUpdated: Date.now()
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving BTC price cache:', error);
    }
  },

  /**
   * Get BTC prices from Supabase for specific dates
   */
  async getFromSupabase(dates: string[]): Promise<BTCPriceCache> {
    try {
      const { data, error } = await supabase
        .from('btc_price_history')
        .select('price_date, price_usd')
        .in('price_date', dates);

      if (error) {
        console.error('Error fetching from Supabase:', error);
        return {};
      }

      const result: BTCPriceCache = {};
      for (const row of data || []) {
        result[row.price_date] = parseFloat(String(row.price_usd));
      }

      return result;
    } catch (error) {
      console.error('Error fetching BTC prices from Supabase:', error);
      return {};
    }
  },

  /**
   * Save a price to Supabase
   */
  async saveToSupabase(date: string, price: number): Promise<void> {
    try {
      const isAuth = await this.isAuthenticated();
      if (!isAuth) {
        console.log('[BTC History] Skipping Supabase save - not authenticated');
        return;
      }

      const { error } = await supabase
        .from('btc_price_history')
        .upsert(
          {
            price_date: date,
            price_usd: price,
            source: 'coingecko',
            fetched_at: new Date().toISOString()
          },
          { onConflict: 'price_date' }
        );

      if (error) {
        console.error('Error saving to Supabase:', error);
      } else {
        console.log(`☁️ BTC price for ${date} saved to Supabase`);
      }
    } catch (error) {
      console.error('Error saving BTC price to Supabase:', error);
    }
  },

  /**
   * Batch save prices to Supabase
   */
  async batchSaveToSupabase(prices: BTCPriceCache): Promise<void> {
    try {
      const isAuth = await this.isAuthenticated();
      if (!isAuth) return;

      const entries = Object.entries(prices);
      if (entries.length === 0) return;

      const rows = entries.map(([date, price]) => ({
        price_date: date,
        price_usd: price,
        source: 'coingecko',
        fetched_at: new Date().toISOString()
      }));

      const { error } = await supabase
        .from('btc_price_history')
        .upsert(rows, { onConflict: 'price_date' });

      if (error) {
        console.error('Error batch saving to Supabase:', error);
      } else {
        console.log(`☁️ ${entries.length} BTC prices saved to Supabase`);
      }
    } catch (error) {
      console.error('Error batch saving BTC prices to Supabase:', error);
    }
  },

  /**
   * Get BTC price for a specific date
   * @param date - Date string in YYYY-MM-DD format
   * @returns BTC price or null if not available
   */
  async getBTCPriceForDate(date: string): Promise<number | null> {
    // 1. Check localStorage cache first (fastest)
    const localCache = this.getLocalCache();
    if (localCache[date]) {
      console.log(`✅ BTC price for ${date} found in local cache: $${localCache[date]}`);
      return localCache[date];
    }

    // 2. Check Supabase if authenticated
    const isAuth = await this.isAuthenticated();
    if (isAuth) {
      const supabasePrices = await this.getFromSupabase([date]);
      if (supabasePrices[date]) {
        console.log(`☁️ BTC price for ${date} found in Supabase: $${supabasePrices[date]}`);
        // Update local cache
        localCache[date] = supabasePrices[date];
        this.saveLocalCache(localCache);
        return supabasePrices[date];
      }
    }

    // 3. Fetch from CoinGecko API
    try {
      console.log(`📡 Fetching BTC price for ${date} from CoinGecko API...`);

      // Convert YYYY-MM-DD to DD-MM-YYYY for CoinGecko
      const [year, month, day] = date.split('-');
      const formattedDate = `${day}-${month}-${year}`;

      const data = await callCoinGeckoAPI('/coins/bitcoin/history', {
        date: formattedDate,
        localization: false
      });

      if (data.market_data && data.market_data.current_price && data.market_data.current_price.usd) {
        const price = data.market_data.current_price.usd;

        // Save to both localStorage and Supabase
        localCache[date] = price;
        this.saveLocalCache(localCache);

        // Save to Supabase (async, don't wait)
        this.saveToSupabase(date, price);

        console.log(`✅ BTC price for ${date}: $${price}`);
        return price;
      }

      console.warn(`⚠️ No BTC price data for ${date}`);
      return null;
    } catch (error) {
      console.error(`❌ Error fetching BTC price for ${date}:`, error);
      return null;
    }
  },

  /**
   * Get BTC prices for multiple dates
   * @param dates - Array of date strings in YYYY-MM-DD format
   * @returns Map of date to price
   */
  async getBTCPricesForDates(dates: string[]): Promise<BTCPriceCache> {
    const uniqueDates = [...new Set(dates)];
    const localCache = this.getLocalCache();
    const result: BTCPriceCache = {};
    let missingDates: string[] = [];

    // 1. Check local cache first
    for (const date of uniqueDates) {
      if (localCache[date]) {
        result[date] = localCache[date];
      } else {
        missingDates.push(date);
      }
    }

    // 2. Check Supabase for missing dates if authenticated
    if (missingDates.length > 0) {
      const isAuth = await this.isAuthenticated();
      if (isAuth) {
        console.log(`☁️ Checking Supabase for ${missingDates.length} missing BTC prices...`);
        const supabasePrices = await this.getFromSupabase(missingDates);

        const stillMissing: string[] = [];
        for (const date of missingDates) {
          if (supabasePrices[date]) {
            result[date] = supabasePrices[date];
            // Update local cache
            localCache[date] = supabasePrices[date];
          } else {
            stillMissing.push(date);
          }
        }

        // Save updated local cache
        if (Object.keys(supabasePrices).length > 0) {
          this.saveLocalCache(localCache);
        }

        missingDates = stillMissing;
      }
    }

    // 3. Fetch remaining missing dates from CoinGecko API
    if (missingDates.length > 0) {
      console.log(`📡 Fetching ${missingDates.length} missing BTC prices from CoinGecko...`);

      const newPrices: BTCPriceCache = {};

      for (const date of missingDates) {
        // Use direct CoinGecko call to avoid recursive Supabase checks
        try {
          const [year, month, day] = date.split('-');
          const formattedDate = `${day}-${month}-${year}`;

          const data = await callCoinGeckoAPI('/coins/bitcoin/history', {
            date: formattedDate,
            localization: false
          });

          if (data.market_data?.current_price?.usd) {
            const price = data.market_data.current_price.usd;
            result[date] = price;
            localCache[date] = price;
            newPrices[date] = price;
            console.log(`✅ BTC price for ${date}: $${price}`);
          }
        } catch (error) {
          console.error(`❌ Error fetching BTC price for ${date}:`, error);
        }

        // Small delay to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      // Save all new prices to localStorage
      if (Object.keys(newPrices).length > 0) {
        this.saveLocalCache(localCache);

        // Batch save to Supabase (async)
        this.batchSaveToSupabase(newPrices);
      }
    }

    return result;
  },

  /**
   * Sync local cache to Supabase
   * Call this after user logs in to push local prices to cloud
   */
  async syncLocalToCloud(): Promise<void> {
    const isAuth = await this.isAuthenticated();
    if (!isAuth) {
      console.log('[BTC History] Cannot sync - not authenticated');
      return;
    }

    const localCache = this.getLocalCache();
    const dates = Object.keys(localCache);

    if (dates.length === 0) {
      console.log('[BTC History] No local prices to sync');
      return;
    }

    console.log(`☁️ Syncing ${dates.length} local BTC prices to Supabase...`);

    // Get existing prices in Supabase
    const existingPrices = await this.getFromSupabase(dates);

    // Find prices that don't exist in Supabase
    const newPrices: BTCPriceCache = {};
    for (const [date, price] of Object.entries(localCache)) {
      if (!existingPrices[date]) {
        newPrices[date] = price;
      }
    }

    if (Object.keys(newPrices).length > 0) {
      await this.batchSaveToSupabase(newPrices);
      console.log(`☁️ Synced ${Object.keys(newPrices).length} new prices to Supabase`);
    } else {
      console.log('[BTC History] All local prices already in Supabase');
    }
  },

  /**
   * Sync cloud prices to local cache
   * Call this after user logs in to pull cloud prices to local
   */
  async syncCloudToLocal(): Promise<void> {
    const isAuth = await this.isAuthenticated();
    if (!isAuth) {
      console.log('[BTC History] Cannot sync - not authenticated');
      return;
    }

    try {
      console.log('☁️ Syncing BTC prices from Supabase to local...');

      // Get all prices from Supabase
      const { data, error } = await supabase
        .from('btc_price_history')
        .select('price_date, price_usd')
        .order('price_date', { ascending: false })
        .limit(1000); // Last 1000 days (~3 years)

      if (error) {
        console.error('Error fetching from Supabase:', error);
        return;
      }

      if (!data || data.length === 0) {
        console.log('[BTC History] No prices in Supabase to sync');
        return;
      }

      // Merge with local cache
      const localCache = this.getLocalCache();
      let newCount = 0;

      for (const row of data) {
        if (!localCache[row.price_date]) {
          localCache[row.price_date] = parseFloat(String(row.price_usd));
          newCount++;
        }
      }

      if (newCount > 0) {
        this.saveLocalCache(localCache);
        console.log(`☁️ Added ${newCount} prices from Supabase to local cache`);
      } else {
        console.log('[BTC History] Local cache already up to date');
      }
    } catch (error) {
      console.error('Error syncing cloud to local:', error);
    }
  },

  /**
   * Full bidirectional sync
   */
  async fullSync(): Promise<void> {
    const isAuth = await this.isAuthenticated();
    if (!isAuth) {
      console.log('[BTC History] Cannot sync - not authenticated');
      return;
    }

    console.log('🔄 Starting full BTC price sync...');

    // First pull from cloud
    await this.syncCloudToLocal();

    // Then push to cloud
    await this.syncLocalToCloud();

    console.log('🔄 BTC price sync complete');
  },

  /**
   * Clear the local cache only
   */
  clearLocalCache(): void {
    localStorage.removeItem(CACHE_KEY);
    console.log('🗑️ BTC price local cache cleared');
  },

  /**
   * Legacy method - alias for getLocalCache
   * @deprecated Use getLocalCache() instead
   */
  getCache(): BTCPriceCache {
    return this.getLocalCache();
  },

  /**
   * Legacy method - alias for saveLocalCache
   * @deprecated Use saveLocalCache() instead
   */
  saveCache(prices: BTCPriceCache): void {
    this.saveLocalCache(prices);
  },

  /**
   * Legacy method - alias for clearLocalCache
   * @deprecated Use clearLocalCache() instead
   */
  clearCache(): void {
    this.clearLocalCache();
  }
};
